/**
 * ➕ SET YACHT DESTINATION PRICING DIALOG
 * Dialog for setting yacht destination pricing (one-way and round-trip)
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Stack,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Place, DirectionsBoat, AccessTime } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { yachtDestinationPricingService, YachtWithPricing, SetYachtPricingData } from '@/services/yachtDestinationPricingService';
import { YachtDestination } from '@/services/yachtDestinationsService';

interface SetYachtPricingDialogProps {
  open: boolean;
  yacht: YachtWithPricing;
  destinations: YachtDestination[];
  onClose: () => void;
  onSuccess: () => void;
}

export const SetYachtPricingDialog: React.FC<SetYachtPricingDialogProps> = ({
  open,
  yacht,
  destinations,
  onClose,
  onSuccess,
}) => {
  const [selectedDestination, setSelectedDestination] = useState('');
  const [oneWayPrice, setOneWayPrice] = useState<number>(0);
  const [roundTripPrice, setRoundTripPrice] = useState<number>(0);
  const [hours, setHours] = useState<number>(0);
  const [minutes, setMinutes] = useState<number>(0);
  const [isAvailable, setIsAvailable] = useState(true);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  // Filter destinations based on yacht location
  const filteredDestinations = destinations.filter(dest => {
    if (!yacht.country) return true;
    return dest.country === yacht.country;
  });

  const handleSubmit = async () => {
    if (!selectedDestination) {
      toast.error('Please select a destination');
      return;
    }

    if (oneWayPrice <= 0 && roundTripPrice <= 0) {
      toast.error('Please set at least one price');
      return;
    }

    const totalMinutes = (hours * 60) + minutes;
    if (totalMinutes <= 0) {
      toast.error('Please set an estimated trip duration');
      return;
    }

    setLoading(true);
    try {
      const data: SetYachtPricingData = {
        destinationId: selectedDestination,
        oneWayPrice: Math.round(oneWayPrice * 100), // Convert to kobo
        roundTripPrice: Math.round(roundTripPrice * 100), // Convert to kobo
        estimatedDurationMinutes: totalMinutes,
        isAvailable,
        notes: notes.trim() || undefined,
      };

      const response = await yachtDestinationPricingService.setPricing(yacht.yachtId, data);
      if (response.success) {
        toast.success('Yacht destination pricing set successfully!');
        onSuccess();
      } else {
        toast.error(response.error || 'Failed to set pricing');
      }
    } catch (error) {
      console.error('Error setting pricing:', error);
      toast.error('Failed to set pricing');
    } finally {
      setLoading(false);
    }
  };

  const selectedDestData = destinations.find(d => d.id === selectedDestination);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Set Yacht Destination Pricing
      </DialogTitle>
      <DialogContent>
        {/* Yacht Info */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600}>
            {yacht.yachtName} - {yacht.yachtType}
          </Typography>
          <Typography variant="caption">
            Capacity: {yacht.capacity} passengers
            {yacht.city && yacht.country ? ` • Homeport: ${yacht.city}, ${yacht.country}` :
             yacht.homeport ? ` • Homeport: ${yacht.homeport}` :
             yacht.city ? ` • Homeport: ${yacht.city}` :
             ' • Location not set'}
          </Typography>
        </Alert>

        {/* Destination Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Destination *</InputLabel>
          <Select
            value={selectedDestination}
            label="Select Destination *"
            onChange={(e) => setSelectedDestination(e.target.value)}
          >
            {filteredDestinations.length === 0 ? (
              <MenuItem disabled>No destinations available for {yacht.country}</MenuItem>
            ) : (
              filteredDestinations.map((dest) => (
                <MenuItem key={dest.id} value={dest.id}>
                  <Stack>
                    <Typography variant="body2" fontWeight={500}>
                      {dest.destinationName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dest.city}, {dest.country}
                      {dest.location.estimatedDistanceKm && ` • ${dest.location.estimatedDistanceKm} km`}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Selected Destination Info */}
        {selectedDestData && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Place fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight={600}>
                {selectedDestData.destinationName}
              </Typography>
              {selectedDestData.location.estimatedDistanceKm && (
                <Chip label={`${selectedDestData.location.estimatedDistanceKm} km`} size="small" />
              )}
            </Stack>
            {selectedDestData.description && (
              <Typography variant="caption" color="text.secondary">
                {selectedDestData.description}
              </Typography>
            )}
          </Box>
        )}

        {/* Pricing Fields */}
        <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
          Pricing (in Naira)
        </Typography>

        <TextField
          fullWidth
          label="One-Way Price"
          type="number"
          value={oneWayPrice || ''}
          onChange={(e) => setOneWayPrice(parseFloat(e.target.value) || 0)}
          InputProps={{
            startAdornment: <InputAdornment position="start">₦</InputAdornment>,
          }}
          sx={{ mb: 2 }}
          helperText="Price for one-way trip to this destination"
        />

        <TextField
          fullWidth
          label="Round-Trip Price"
          type="number"
          value={roundTripPrice || ''}
          onChange={(e) => setRoundTripPrice(parseFloat(e.target.value) || 0)}
          InputProps={{
            startAdornment: <InputAdornment position="start">₦</InputAdornment>,
          }}
          sx={{ mb: 2 }}
          helperText="Price for round-trip to this destination"
        />

        {/* Duration Estimate */}
        <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
          Estimated Trip Duration *
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Hours"
            type="number"
            value={hours || ''}
            onChange={(e) => setHours(parseInt(e.target.value) || 0)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><AccessTime /></InputAdornment>,
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Minutes"
            type="number"
            value={minutes || ''}
            onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
            InputProps={{
              inputProps: { min: 0, max: 59 }
            }}
            sx={{ flex: 1 }}
          />
        </Stack>

        {/* Availability Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={isAvailable}
              onChange={(e) => setIsAvailable(e.target.checked)}
            />
          }
          label="Available for booking"
          sx={{ mb: 2 }}
        />

        {/* Notes */}
        <TextField
          fullWidth
          label="Notes (Optional)"
          multiline
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any internal notes about this pricing..."
          helperText="These notes are only visible to admins"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedDestination || ((oneWayPrice <= 0 && roundTripPrice <= 0) || (hours === 0 && minutes === 0))}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? 'Setting...' : 'Set Pricing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetYachtPricingDialog;
